// What a granted (company-access) admin can open. Everything else still shows
// in the menus and Settings tabs, greyed out. To open a page or tab to granted
// admins, add it here AND add the API routes it calls to the allowlist in
// apps/api/src/middleware/company-operational-access.js, or it will load and
// then fail with "Operation unavailable in company view".
const operationalPages = new Set([
  '/home', '/dashboard', '/tablet-view', '/barrel-inventory', '/facility-inventory',
  '/wash-packages', '/wash-packages-cost', '/wash-count', '/cost-per-wash',
  '/graphs', '/statistics', '/admin', '/admin/injector-tips',
  '/admin/wash-package-counts', '/admin/average-volume', '/admin/daily-revenue', '/chem-usage', '/audit-log'
]);
const configurationTabs = new Set(['settings', 'system-settings', 'sensors', 'inventory-config', 'packages']);

// SH1 owner share: the data groups each page needs (apps/api/src/middleware/share-data-groups.js decides
// per API call; this only avoids offering pages that would come back empty or refused).
const ownerSharePages = {
  // Pages list every data group their API calls need (review S4). /dashboard is the landing page and always opens
  // (its summary is trimmed to the shared groups).
  '/home': ['equipment', 'usage'], '/dashboard': [],
  '/tablet-view': ['usage'], '/graphs': ['usage'], '/statistics': ['usage'],
  '/barrel-inventory': ['inventory'], '/facility-inventory': ['inventory'],
  '/wash-packages': ['washes'], '/wash-count': ['washes'], '/admin/wash-package-counts': ['washes'],
  '/wash-packages-cost': ['washes', 'inventory'], '/cost-per-wash': ['washes', 'inventory', 'usage'],
  '/admin/average-volume': ['washes', 'usage'],
  '/admin/daily-revenue': ['washes'],
  '/admin': ['equipment'], '/admin/injector-tips': ['equipment'],
  '/chem-usage': ['inventory', 'usage', 'equipment'], '/audit-log': ['equipment', 'inventory', 'usage', 'washes']
};
// Regional Settings is account-wide (owner only), so it is not offered through a share (review B1).
// alerts: a company manages its own alert recipients at its shared sites, on any share (SH8).
const ownerShareTabs = { alerts: [], chronological: ['equipment'], settings: ['equipment'],
  sensors: ['equipment'], 'inventory-config': ['inventory'], packages: ['washes'] };

// Pages whose API calls need all usage history (the yearly usage summary is refused under a 30-day share).
const needsAllUsageHistory = new Set(['/chem-usage']);

// A page opens if at least one shared location includes every group it needs.
function ownerShareAllows(userData, groups, needsManage = false, allUsageHistory = false) {
  const locations = Object.values(userData?.shareScope?.locations || {});
  return locations.some(terms => groups.every(group => terms.data?.[group] === true) &&
    (!needsManage || terms.level === 'manage') && (!allUsageHistory || terms.history?.usage === 'all'));
}

export function companyCsvAllowed(companyView, userData) {
  if (!companyView) return true;
  if (userData?.companyAccessMode === 'owner-share') return ownerShareAllows(userData, ['usage']);
  return userData?.companyAccessMode === 'operational-admin' &&
    userData?.permissions?.chemicalMonitoring?.view === true;
}

export function companyPageAllowed(companyView, userData, path) {
  if (!companyView) return true;
  if (path === '/dashboard') return true;
  if (userData?.companyAccessMode === 'owner-share') {
    const groups = ownerSharePages[path];
    return Array.isArray(groups) && ownerShareAllows(userData, groups, false, needsAllUsageHistory.has(path));
  }
  if (userData?.companyAccessMode !== 'operational-admin') return false;
  if ((path === '/admin' || path === '/admin/injector-tips') &&
      userData?.permissions?.chemicalMonitoring?.configure !== true) return false;
  return operationalPages.has(path);
}

export function companyAdminTabAllowed(companyView, userData, tab) {
  if (!companyView) return true;
  if (userData?.companyAccessMode === 'owner-share') {
    const groups = ownerShareTabs[tab];
    return Array.isArray(groups) && ownerShareAllows(userData, groups, !['chronological', 'alerts'].includes(tab));
  }
  if (userData?.companyAccessMode !== 'operational-admin') return false;
  return tab === 'chronological' || tab === 'alerts' || (configurationTabs.has(tab) &&
    userData?.permissions?.chemicalMonitoring?.configure === true);
}
